import { Fragment, useState, useMemo, useCallback, useEffect } from "react";
import cn from "classnames";
import UploadIcon from "@icons/upload-01.svg?react";
import HDDialog from "../HDDialog";
import { VITE_PUBLIC_ENV } from "@/common/utils/constants";
import IncompleteUploads from "./IncompleteUploads";
import LocalRecordings from "./LocalRecordings";
import Tabs from "@/common/components/HDTabs";
import { PatientRecord, HDTab, TranscriptSectionStatus } from "@/common/utils/types";
import AudioDb, { TranscriptSectionIndexDB } from "../../classes/audioDb";
import { toast } from "react-toastify";
import { useSession } from "@supabase/auth-helpers-react";
import useFileUpload from "../../hooks/useFileUpload";
import { useAppDispatch, useAppSelector } from "@/common/hooks/useRedux";
import { updateTranscriptSectionStatus } from "@redux/patientTableRecordsSlice";
import { uploadAndTranscribeBulkAudio } from "../../lib/supabaseClient";
import * as Sentry from "@sentry/react";
import ConfirmModal from "../ConfirmModal";
import Trash03Icon from "@icons/trash-03.svg?react";
import { useDataContextApi } from "@/state/providers/DataProvider";
import { useAccountContext } from "@/state/providers/AccountProvider";
import { verifyAudioBlobIntegrity, buildDeadBlobSentryExtra } from "../../lib/audioBlobIntegrity";

/*
Notes:
    - Incomplete uploads are stored in indexed db, see AudioDb class
    - AudioDb is somewhat separate from the rest of the app, so state is a bit weird
    - Poll the db for incomplete uploads every 30 seconds
*/
const LocalStorageModal: React.FC<{
    disabled: boolean;
    open: boolean;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
    showButton?: boolean;
}> = ({ disabled, open, setOpen, showButton = true }) => {
    const [activeTabId, setActiveTabId] = useState<"incomplete" | "local">("incomplete");
    const { uploadAudio } = useFileUpload();
    const { accountData } = useAccountContext();
    const session = useSession();
    const { selectedDocuments } = useAppSelector((state) => state.documents);
    const dispatch = useAppDispatch();
    const { incompleteUploads, setIncompleteUploads } = useDataContextApi();
    const [recordings, setRecordings] = useState<TranscriptSectionIndexDB[]>([]);
    const [recordingToDelete, setRecordingToDelete] = useState<TranscriptSectionIndexDB | null>(null);

    const loadRecordings = useCallback(async () => {
        try {
            const audioDb = AudioDb.getInstance();
            const allRecordings = await audioDb.getRecordings();
            setRecordings(allRecordings);
            // Sync global incompleteUploads state
            setIncompleteUploads(
                allRecordings.filter((recording) => recording.bytesUploaded < recording.audioData.size),
            );
        } catch (error) {
            console.error("Failed to load recordings:", error);
        }
    }, []);

    // Reload recordings when the modal is opened
    useEffect(() => {
        open && loadRecordings();
    }, [loadRecordings, open]);

    const tabs: HDTab[] = useMemo(
        () => [
            { header: "Incomplete Uploads", id: "incomplete", isActive: activeTabId === "incomplete" },
            { header: "Local Recordings", id: "local", isActive: activeTabId === "local" },
        ],
        [activeTabId],
    );

    const handleTabChange = (tab: string) => {
        if (tab === "incomplete" || tab === "local") {
            setActiveTabId(tab);
        }
    };

    const handleUpload = useCallback(
        async (recording: TranscriptSectionIndexDB, existingPatientRecord: PatientRecord | null) => {
            try {
                const integrity = await verifyAudioBlobIntegrity(recording.audioData);
                if (integrity.kind === "dead") {
                    Sentry.captureException(new Error("recording.dead_blob_detected_on_retry_upload"), {
                        tags: {
                            errorCategory: "recording",
                            errorId: "recording.dead_blob_detected_on_retry_upload",
                        },
                        extra: buildDeadBlobSentryExtra(integrity, {
                            transcriptId: recording.transcriptId,
                            transcriptSectionId: recording.transcriptSectionId,
                            claimedSize: recording.audioData.size,
                        }),
                    });
                    toast.error(
                        `A saved recording (${recording.fileName}) was lost by the browser and can't be recovered.`,
                        { autoClose: false },
                    );
                    const localAudioDb = AudioDb.getInstance();
                    await localAudioDb.deleteTranscriptSectionAudio(
                        recording.transcriptId,
                        recording.transcriptSectionId,
                    );
                    await loadRecordings();
                    return;
                }

                let uploadError = null;

                Sentry.addBreadcrumb({
                    message: "Retrying upload",
                    level: "info",
                    data: {
                        userId: session?.user.id,
                        email: session?.user.email,
                        patientName: recording?.patientName,
                        patientRecordId: recording?.patientRecordId,
                        transcriptSectionId: recording.transcriptSectionId,
                    },
                });

                let patientRecord = existingPatientRecord;
                if (!patientRecord) {
                    patientRecord = {
                        id: recording.patientRecordId,
                        status: TranscriptSectionStatus.InProgress,
                        transcriptions: {
                            id: recording.transcriptId,
                        },
                    };
                }

                const audioFile = new File([recording.audioData], recording.fileName, {
                    type: recording.audioData.type,
                });
                // Pass the target Monitoring document AND account persisted alongside this recording
                // (see TranscriptSectionIndexDB), not the live recordingMonitoringDocumentIdRef /
                // account context — this retry can run minutes/hours/a session after the recording,
                // by which point either live value may reflect a different, unrelated recording (or
                // clinic account, EN-1171) than the one actually captured here.
                //
                // PR review finding: recording.accountId is only populated for recordings captured
                // after EN-1216 added the field -- older rows (persisted before that deploy) read
                // back as undefined and, unguarded, would hard-fail every retry forever (the refuse-
                // without-accountId guards this PR added have no fallback of their own). Falling
                // back to the LIVE account here only when nothing was captured is safe: it doesn't
                // touch the EN-1171 case (a real captured recording.accountId always wins), it only
                // fires for recordings with no captured intent to protect in the first place.
                const resolvedAccountId = recording.accountId ?? accountData?.id ?? null;
                const { err, is409 } = await uploadAudio(
                    audioFile,
                    recording.fileName,
                    recording.transcriptId,
                    recording.transcriptSectionId,
                    patientRecord,
                    recording.targetMonitoringDocumentId ?? null,
                    resolvedAccountId,
                );
                uploadError = err;

                if (is409) {
                    toast.info(`${recording.fileName} is already attempting to upload. Try again later!`);
                    return;
                }

                if (uploadError) {
                    // PR review finding: the upfront check above only proves the blob was alive at
                    // the START of this call -- uploadAudio() can fail for a reason unrelated to
                    // integrity (network, auth, ...), but it can ALSO fail because ITS OWN internal
                    // integrity check (Task 4) caught the blob dying mid-attempt, sometime after our
                    // upfront check passed. Either way, uploadError is truthy and looks the same
                    // here -- string-matching uploadAudio's error message to tell them apart would
                    // be fragile. Re-verify directly instead: this raw fallback has no integrity
                    // check of its own, so if the blob is dead NOW, refuse rather than upload the
                    // husk it would otherwise upload here and report as a success.
                    const fallbackIntegrity = await verifyAudioBlobIntegrity(recording.audioData);
                    if (fallbackIntegrity.kind === "dead") {
                        Sentry.captureException(new Error("recording.dead_blob_detected_on_retry_upload_fallback"), {
                            tags: {
                                errorCategory: "recording",
                                errorId: "recording.dead_blob_detected_on_retry_upload_fallback",
                            },
                            extra: buildDeadBlobSentryExtra(fallbackIntegrity, {
                                transcriptId: recording.transcriptId,
                                transcriptSectionId: recording.transcriptSectionId,
                                claimedSize: recording.audioData.size,
                            }),
                        });
                        toast.error(
                            `A saved recording (${recording.fileName}) was lost by the browser and can't be recovered.`,
                            { autoClose: false },
                        );
                        const localAudioDb = AudioDb.getInstance();
                        await localAudioDb.deleteTranscriptSectionAudio(
                            recording.transcriptId,
                            recording.transcriptSectionId,
                        );
                        await loadRecordings();
                        return;
                    }

                    Sentry.addBreadcrumb({
                        message: "Error uploading, retrying with uploadAndTranscribeBulkAudio",
                        level: "error",
                        data: {
                            userId: session?.user.id,
                            email: session?.user.email,
                            patientName: recording?.patientName,
                            patientRecordId: recording?.patientRecordId,
                        },
                    });

                    const { error: error2 } = await uploadAndTranscribeBulkAudio(
                        recording.transcriptId,
                        recording.transcriptSectionId,
                        recording.audioData,
                        recording.fileName,
                        recording.patientRecordId,
                        recording?.patientName || "",
                        "",
                        selectedDocuments,
                        recording.targetMonitoringDocumentId ?? null,
                        resolvedAccountId,
                    );
                    if (error2) {
                        throw error2;
                    }
                }

                dispatch(
                    updateTranscriptSectionStatus({
                        id: recording.patientRecordId,
                        sectionId: recording.transcriptSectionId,
                        status: TranscriptSectionStatus.Uploaded,
                    }),
                );

                // delete uploaded audio from indexedDB if running low on space
                const localAudioDb = AudioDb.getInstance();
                await localAudioDb.handleUploadedTranscriptSectionAudio(
                    recording.transcriptId,
                    recording.transcriptSectionId,
                );

                toast.success(`Successfully uploaded ${recording.fileName}`);

                // Refresh recordings after successful upload
                await loadRecordings();
            } catch (error) {
                toast.error(`Uh Oh, something went wrong! ${recording.fileName}`);
                Sentry.captureException(error);
            }
        },
        // PR review finding: accountData?.id is read inside this callback (resolvedAccountId's
        // live-account fallback) but was missing here -- a useCallback with a stale dep array
        // keeps its ORIGINAL closure until a listed dep changes, so if the modal mounts before
        // the clinic account resolves (accountData still `{}`), or the user later switches
        // clinics, this callback could keep using a stale/undefined id for the rest of its
        // lifetime instead of the current one. react-hooks/exhaustive-deps is off repo-wide, so
        // this isn't auto-caught -- deps here are manual and must be kept complete by hand.
        [uploadAudio, session?.user.id, selectedDocuments, dispatch, loadRecordings, accountData?.id],
    );

    const handleDownload = useCallback(async (recording: TranscriptSectionIndexDB) => {
        try {
            const integrity = await verifyAudioBlobIntegrity(recording.audioData);
            if (integrity.kind === "dead") {
                toast.error(
                    "This recording's audio was lost by the browser and can't be recovered. Downloading it will not help.",
                    { autoClose: false },
                );
                Sentry.captureException(new Error("recording.dead_blob_detected_on_download"), {
                    tags: { errorCategory: "recording", errorId: "recording.dead_blob_detected_on_download" },
                    extra: buildDeadBlobSentryExtra(integrity, {
                        context: "LocalStorageModal.handleDownload",
                        transcriptId: recording.transcriptId,
                        transcriptSectionId: recording.transcriptSectionId,
                        claimedSize: recording.audioData.size,
                    }),
                });
                return;
            }
            const audioFile = new File([recording.audioData], recording.fileName, { type: recording.audioData.type });
            const url = URL.createObjectURL(audioFile);
            const link = document.createElement("a");
            link.href = url;
            link.download = recording.fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (error) {
            toast.error("Failed to download audio file");
            Sentry.captureException(error, { extra: { context: "LocalStorageModal.handleDownload" } });
        }
    }, []);

    const handleDeleteClick = useCallback(async (recording: TranscriptSectionIndexDB) => {
        setRecordingToDelete(recording);
    }, []);

    const handleConfirmDelete = useCallback(async () => {
        if (!recordingToDelete) return;

        try {
            const localAudioDb = AudioDb.getInstance();
            await localAudioDb.deleteTranscriptSectionAudio(
                recordingToDelete.transcriptId,
                recordingToDelete.transcriptSectionId,
            );
            toast.success(`Deleted ${recordingToDelete.fileName}`);
            // Refresh recordings after delete
            await loadRecordings();

            setRecordingToDelete(null);
        } catch (error) {
            toast.error("Failed to delete recording");
            Sentry.captureException(error, { extra: { context: "LocalStorageModal.handleConfirmDelete" } });
            setRecordingToDelete(null);
        }
    }, [recordingToDelete, loadRecordings]);

    // Lazy load test utilities only in non-production environments
    const handleAddTestUpload = async () => {
        if (VITE_PUBLIC_ENV === "production") return;

        try {
            const { addTestIncompleteUpload } = await import("../../utils/testAudioStorage");
            await addTestIncompleteUpload();
            // Refresh the recordings list
            await loadRecordings();
        } catch (error) {
            console.error("Failed to add test upload:", error);
        }
    };

    return (
        <Fragment>
            {showButton && (
                <button
                    id="incomplete-upload-modal"
                    onClick={() => setOpen(!open)}
                    disabled={disabled}
                    className={cn(
                        disabled ? "opacity-50 cursor-not-allowed" : "bg-gray-100 text-gray-900 hover:bg-gray-200",
                        "rounded bg-white grow px-4 py-2.5 text-[10px] sm:text-sm font-semibold bg-gray-100 hover:bg-gray-200 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300",
                    )}
                >
                    <span className="flex flex-row justify-center items-center whitespace-nowrap">
                        <UploadIcon className="h-4 w-4 mr-1 text-gray-900" />
                        <span className="sm:hidden">Upload</span>
                        <span className="hidden sm:inline">Incomplete Uploads</span>

                        {incompleteUploads &&
                            // Use optional chaining with filter and immediately get the length
                            // If incompleteUploads is null, this will short-circuit and return 0
                            incompleteUploads.filter((upload) => !upload.isUploading).length > 0 && (
                                <span className="ml-1 text-white px-2 py-0 bg-red-500 rounded-full">
                                    {incompleteUploads.filter((upload) => !upload.isUploading).length}
                                </span>
                            )}
                    </span>
                </button>
            )}

            <HDDialog
                isOpen={open}
                handleClose={() => setOpen(false)}
                header="Local Storage"
                className="sm:max-w-3xl"
                subHeader={
                    <div className="overflow-x-auto mb-[-17px]">
                        <Tabs tabs={tabs} changeTab={handleTabChange} />
                    </div>
                }
                id="local-storage-modal-dialog"
            >
                {activeTabId === "incomplete" ? (
                    <IncompleteUploads
                        incompleteUploads={incompleteUploads}
                        handleAddTestUpload={handleAddTestUpload}
                        handleUpload={handleUpload}
                        handleDownload={handleDownload}
                        handleDelete={handleDeleteClick}
                    />
                ) : (
                    <LocalRecordings
                        recordings={recordings}
                        handleAddTestUpload={handleAddTestUpload}
                        handleUpload={handleUpload}
                        handleDownload={handleDownload}
                        handleDelete={handleDeleteClick}
                    />
                )}
            </HDDialog>

            <ConfirmModal
                id="DeleteRecording"
                headerText="Delete Locally Stored Recording"
                content={
                    <div className="flex flex-col gap-2 text-gray-900">
                        {recordingToDelete && (
                            <>
                                <p>
                                    Are you sure you want to delete
                                    <span className={cn(recordingToDelete.fileName && "font-bold")}>
                                        &nbsp;{recordingToDelete.fileName || "this recording"}
                                    </span>
                                    {recordingToDelete.patientName && (
                                        <span>
                                            &nbsp;for patient{" "}
                                            <span className="font-bold">{recordingToDelete.patientName}</span>
                                        </span>
                                    )}
                                    ?
                                </p>
                                {recordingToDelete.bytesUploaded < recordingToDelete.audioData.size ? (
                                    <p className="text-red-600 font-semibold">
                                        This recording has not been fully uploaded to HappyDoc and has not been
                                        transcribed.
                                    </p>
                                ) : (
                                    <p>This recording has been uploaded to HappyDoc and is safe to delete locally.</p>
                                )}
                            </>
                        )}
                        <p>This action cannot be undone.</p>
                    </div>
                }
                cancelButtonText="Cancel"
                confirmButtonText="Delete"
                confirmButtonColor="bg-red-600"
                confirmHoverButtonColor="bg-red-500"
                ConfirmButtonIcon={Trash03Icon}
                onCancel={() => {
                    setRecordingToDelete(null);
                }}
                onConfirm={handleConfirmDelete}
                isOpen={!!recordingToDelete}
            />
        </Fragment>
    );
};

export default LocalStorageModal;
